import { FormEvent, useEffect, useMemo, useState } from "react";
import type {
  AuthUser,
  BalanceSnapshotDto,
  ChildAccountDto,
  DashboardDto,
  HoldingDto,
  InstrumentDto,
  LedgerEntryDto,
  MeDto,
  MoneyRequestDto,
} from "../shared/types";
import { INVESTMENT_RESULTS_PER_WEEK } from "../shared/types";

type LoginMode = "child" | "parent";
type ChildTab = "money" | "investments";
type Status = { kind: "idle" | "success" | "error"; message: string };

interface DashboardResponse extends DashboardDto {
  notice: string;
}

const currency = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
});

export default function App() {
  const [me, setMe] = useState<MeDto>({ user: null, csrfToken: null });
  const [dashboard, setDashboard] = useState<DashboardResponse | null>(null);
  const [requests, setRequests] = useState<MoneyRequestDto[]>([]);
  const [children, setChildren] = useState<ChildAccountDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<Status>({ kind: "idle", message: "" });

  async function refresh() {
    const nextMe = await api<MeDto>("/api/me");
    setMe(nextMe);

    if (nextMe.user) {
      const data = await api<DashboardResponse>("/api/child/dashboard");
      setDashboard(data);

      if (nextMe.user.role === "parent") {
        const [parentRequests, parentChildren] = await Promise.all([
          api<{ requests: MoneyRequestDto[] }>("/api/parent/requests"),
          api<{ children: ChildAccountDto[] }>("/api/parent/children"),
        ]);
        setRequests(parentRequests.requests);
        setChildren(parentChildren.children);
      } else {
        setRequests([]);
        setChildren([]);
      }
    } else {
      setDashboard(null);
      setRequests([]);
      setChildren([]);
    }
  }

  useEffect(() => {
    refresh()
      .catch((error) => setStatus({ kind: "error", message: error.message }))
      .finally(() => setLoading(false));
  }, []);

  async function afterMutation(message: string) {
    await refresh();
    setStatus({ kind: "success", message });
  }

  if (loading) {
    return <LoadingScreen />;
  }

  if (!me.user) {
    return (
      <LoginScreen
        status={status}
        onLogin={async (user, csrfToken) => {
          setMe({ user, csrfToken });
          await refresh();
        }}
      />
    );
  }

  return (
    <main className="shell">
      <AppHeader
        user={me.user}
        csrfToken={me.csrfToken}
        onLogout={async () => {
          await api("/api/auth/logout", {
            method: "POST",
            csrfToken: me.csrfToken,
          });
          setMe({ user: null, csrfToken: null });
          setDashboard(null);
          setRequests([]);
          setChildren([]);
        }}
      />

      {status.message ? <StatusBanner status={status} /> : null}

      {dashboard ? (
        me.user.role === "child" ? (
          <ChildDashboard
            dashboard={dashboard}
            csrfToken={me.csrfToken}
            onDone={afterMutation}
            onRefresh={refresh}
            onError={(message) => setStatus({ kind: "error", message })}
          />
        ) : (
          <ParentDashboard
            dashboard={dashboard}
            requests={requests}
            children={children}
            csrfToken={me.csrfToken}
            onDone={afterMutation}
            onError={(message) => setStatus({ kind: "error", message })}
          />
        )
      ) : (
        <EmptyState />
      )}
    </main>
  );
}

function LoginScreen({
  status,
  onLogin,
}: {
  status: Status;
  onLogin: (user: AuthUser, csrfToken: string) => Promise<void>;
}) {
  const [mode, setMode] = useState<LoginMode>("child");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError("");
    const form = new FormData(event.currentTarget);
    const endpoint =
      mode === "child" ? "/api/auth/child/login" : "/api/auth/parent/login";
    const payload =
      mode === "child"
        ? {
            username: String(form.get("username") ?? ""),
            pin: String(form.get("password") ?? ""),
          }
        : {
            email: String(form.get("email") ?? ""),
            password: String(form.get("password") ?? ""),
          };

    try {
      const result = await api<{ user: AuthUser; csrfToken: string }>(endpoint, {
        method: "POST",
        body: payload,
      });
      await onLogin(result.user, result.csrfToken);
    } catch (loginError) {
      setError(loginError instanceof Error ? loginError.message : "Login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="login-page">
      <section className="login-panel">
        <div>
          <p className="eyebrow">Edfolio</p>
          <h1>Your money map</h1>
          <p className="login-copy">
            Save, ask your banker for changes, and try pretend investments that
            teach how balances move.
          </p>
        </div>

        <div className="login-art" aria-hidden="true">
          <div className="coin coin-a">$</div>
          <div className="coin coin-b">%</div>
          <div className="ledger-lines">
            <span />
            <span />
            <span />
            <span />
          </div>
        </div>

        <form className="login-form" onSubmit={submit}>
          <div className="segmented" aria-label="Choose login type">
            <button
              type="button"
              className={mode === "child" ? "active" : ""}
              onClick={() => setMode("child")}
            >
              Child
            </button>
            <button
              type="button"
              className={mode === "parent" ? "active" : ""}
              onClick={() => setMode("parent")}
            >
              Parent
            </button>
          </div>

          {mode === "child" ? (
            <>
              <label>
                Username
                <input name="username" autoComplete="username" required />
              </label>
              <label>
                Password
                <input
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                />
              </label>
            </>
          ) : (
            <>
              <label>
                Email
                <input name="email" type="email" autoComplete="email" required />
              </label>
              <label>
                Password
                <input
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                />
              </label>
            </>
          )}

          {error ? <p className="form-error">{error}</p> : null}
          {status.message && status.kind === "error" ? (
            <p className="form-error">{status.message}</p>
          ) : null}
          <button className="primary-button" disabled={busy}>
            {busy ? "Checking..." : "Log in"}
          </button>
        </form>
      </section>
    </main>
  );
}

function ChildDashboard({
  dashboard,
  csrfToken,
  onDone,
  onRefresh,
  onError,
}: {
  dashboard: DashboardResponse;
  csrfToken: string | null;
  onDone: (message: string) => Promise<void>;
  onRefresh: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [activeTab, setActiveTab] = useState<ChildTab>("money");
  const [clock, setClock] = useState(() => Date.now());
  const nextResultAt = useMemo(() => soonestNextResultAt(dashboard.holdings), [dashboard.holdings]);

  useEffect(() => {
    const interval = window.setInterval(() => setClock(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!nextResultAt) {
      return;
    }

    const nextResultMs = Date.parse(nextResultAt);
    if (!Number.isFinite(nextResultMs)) {
      return;
    }

    const delayMs = Math.max(1000, nextResultMs - Date.now() + 700);
    const timeout = window.setTimeout(() => {
      onRefresh().catch((error) => {
        onError(error instanceof Error ? error.message : "Investment refresh failed");
      });
    }, delayMs);

    return () => window.clearTimeout(timeout);
  }, [nextResultAt, onError, onRefresh]);

  return (
    <>
      <Overview dashboard={dashboard} />
      <nav className="child-tabs" aria-label="Child dashboard sections">
        <button
          type="button"
          className={activeTab === "money" ? "active" : ""}
          onClick={() => setActiveTab("money")}
        >
          <span aria-hidden="true">💼</span>
          Money dashboard
        </button>
        <button
          type="button"
          className={activeTab === "investments" ? "active" : ""}
          onClick={() => setActiveTab("investments")}
        >
          <span aria-hidden="true">◔</span>
          Investment management
        </button>
      </nav>

      {activeTab === "money" ? (
        <>
          <section className="workspace two-column">
            <BalanceGraph history={dashboard.history} />
            <RequestPanel csrfToken={csrfToken} onDone={onDone} onError={onError} />
          </section>
          <section className="workspace">
            <ActivityPanel
              transactions={dashboard.transactions}
              pendingRequests={dashboard.pendingRequests}
            />
          </section>
        </>
      ) : (
        <section className="workspace investment-workspace">
          <InvestmentPanel
            csrfToken={csrfToken}
            instruments={dashboard.instruments}
            holdings={dashboard.holdings}
            cashCents={dashboard.balance.cashCents}
            nowMs={clock}
            onDone={onDone}
            onError={onError}
          />
        </section>
      )}
      <footer className="notice">
        <span aria-hidden="true">🛡</span>
        <p>{dashboard.notice}</p>
      </footer>
    </>
  );
}

function ParentDashboard({
  dashboard,
  requests,
  children,
  csrfToken,
  onDone,
  onError,
}: {
  dashboard: DashboardResponse;
  requests: MoneyRequestDto[];
  children: ChildAccountDto[];
  csrfToken: string | null;
  onDone: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  return (
    <>
      <Overview dashboard={dashboard} />
      <section className="workspace parent-tools">
        <ParentRequests
          requests={requests}
          csrfToken={csrfToken}
          onDone={onDone}
          onError={onError}
        />
        <ChildManagement
          children={children}
          csrfToken={csrfToken}
          onDone={onDone}
          onError={onError}
        />
        <ParentEntry csrfToken={csrfToken} onDone={onDone} onError={onError} />
      </section>
      <section className="workspace two-column">
        <BalanceGraph history={dashboard.history} />
        <ActivityPanel
          transactions={dashboard.transactions}
          pendingRequests={dashboard.pendingRequests}
        />
      </section>
      <footer className="notice">
        <span aria-hidden="true">🛡</span>
        <p>{dashboard.notice}</p>
      </footer>
    </>
  );
}

function Overview({ dashboard }: { dashboard: DashboardResponse }) {
  return (
    <section className="overview">
      <div className="overview-balance">
        <p className="eyebrow">{dashboard.account.displayName}</p>
        <h2>{money(dashboard.balance.totalCents)}</h2>
        <p>Total balance</p>
      </div>
      <div className="piggy-scene" aria-hidden="true">
        <span className="sparkle sparkle-a">✦</span>
        <span className="sparkle sparkle-b">✦</span>
        <span className="coin-pop">$</span>
        <span className="piggy">🐷</span>
      </div>
      <div className="metric-row" aria-label="Balance details">
        <Metric
          label="Ready to use"
          value={money(dashboard.balance.cashCents)}
          tone="cash"
          icon="👛"
        />
        <Metric
          label="Invested"
          value={money(dashboard.balance.investedCents)}
          tone="invested"
          icon="📊"
        />
        <Metric
          label="Pending"
          value={String(dashboard.pendingRequests.length)}
          tone="pending"
          icon="⌛"
        />
      </div>
    </section>
  );
}

function Metric({
  label,
  value,
  tone,
  icon,
}: {
  label: string;
  value: string;
  tone: "cash" | "invested" | "pending";
  icon: string;
}) {
  return (
    <div className={`metric ${tone}`}>
      <span className="metric-label">{label}</span>
      <strong>{value}</strong>
      <span className="metric-icon" aria-hidden="true">
        {icon}
      </span>
    </div>
  );
}

function BalanceGraph({ history }: { history: BalanceSnapshotDto[] }) {
  const points = useMemo(() => graphPoints(history), [history]);
  const latest = history.at(-1);

  return (
    <section className="panel graph-panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Balance</p>
          <h3>Growth over time</h3>
        </div>
        {latest ? <strong>{money(latest.totalCents)}</strong> : null}
      </div>
      <svg className="balance-chart" viewBox="0 0 640 300" role="img" aria-label="Balance history chart">
        <line x1="58" y1="48" x2="610" y2="48" className="chart-grid" />
        <line x1="58" y1="126" x2="610" y2="126" className="chart-grid" />
        <line x1="58" y1="204" x2="610" y2="204" className="chart-grid" />
        <line x1="58" y1="262" x2="610" y2="262" className="axis" />
        <text x="18" y="52" className="chart-label">$60</text>
        <text x="18" y="130" className="chart-label">$40</text>
        <text x="18" y="208" className="chart-label">$20</text>
        <text x="24" y="266" className="chart-label">$0</text>
        {points.area ? <path d={points.area} className="chart-area" /> : null}
        {points.line ? <path d={points.line} className="chart-line" /> : null}
        {points.circles.map((point) => (
          <circle key={`${point.x}-${point.y}`} cx={point.x} cy={point.y} r="5" />
        ))}
      </svg>
      {history.length === 0 ? (
        <p className="muted">No balance history yet.</p>
      ) : (
        <p className="muted">Pending requests appear after the parent approves them.</p>
      )}
    </section>
  );
}

function RequestPanel({
  csrfToken,
  onDone,
  onError,
}: {
  csrfToken: string | null;
  onDone: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  return (
    <section className="panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Ask the banker</p>
          <h3>Deposit or withdrawal</h3>
        </div>
      </div>
      <MoneyRequestForm
        title="Deposit"
        icon="↓"
        tone="deposit"
        endpoint="/api/requests/deposit"
        csrfToken={csrfToken}
        onDone={() => onDone("Deposit request sent to your parent.")}
        onError={onError}
      />
      <MoneyRequestForm
        title="Withdrawal"
        icon="↑"
        tone="withdrawal"
        endpoint="/api/requests/withdrawal"
        csrfToken={csrfToken}
        onDone={() => onDone("Withdrawal request sent to your parent.")}
        onError={onError}
      />
    </section>
  );
}

function MoneyRequestForm({
  title,
  icon,
  tone,
  endpoint,
  csrfToken,
  onDone,
  onError,
}: {
  title: string;
  icon: string;
  tone: "deposit" | "withdrawal";
  endpoint: string;
  csrfToken: string | null;
  onDone: () => Promise<void>;
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    setBusy(true);
    const form = new FormData(formElement);

    try {
      await api(endpoint, {
        method: "POST",
        csrfToken,
        body: {
          amount: String(form.get("amount") ?? ""),
          note: String(form.get("note") ?? ""),
        },
      });
      formElement.reset();
      await onDone();
    } catch (error) {
      onError(error instanceof Error ? error.message : "Request failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className={`compact-form request-form ${tone}`} onSubmit={submit}>
      <h4>
        <span aria-hidden="true">{icon}</span>
        {title}
      </h4>
      <div className="inline-fields">
        <label>
          Amount
          <input name="amount" inputMode="decimal" placeholder="12.50" required />
        </label>
        <label>
          Note (Why?)
          <input name="note" maxLength={160} placeholder="Why?" />
        </label>
      </div>
      <button className="secondary-button request-button" disabled={busy}>
        {busy ? "Sending..." : `Request ${title.toLowerCase()}  ✈`}
      </button>
    </form>
  );
}

function InvestmentPanel({
  csrfToken,
  instruments,
  holdings,
  cashCents,
  nowMs,
  onDone,
  onError,
}: {
  csrfToken: string | null;
  instruments: InstrumentDto[];
  holdings: HoldingDto[];
  cashCents: number;
  nowMs: number;
  onDone: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const nextResultAt = soonestNextResultAt(holdings);

  return (
    <section className="panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Invest</p>
          <h3>Investment management</h3>
        </div>
        <div className="investment-status">
          <strong>{money(cashCents)} cash</strong>
          <span>
            {nextResultAt
              ? `Next result in ${timeRemaining(nextResultAt, nowMs)}`
              : `${INVESTMENT_RESULTS_PER_WEEK} steps per week`}
          </span>
        </div>
      </div>
      <div className="instrument-list">
        {instruments.map((instrument) => (
          <InstrumentRow
            key={instrument.id}
            instrument={instrument}
            holding={holdings.find((item) => item.instrumentId === instrument.id)}
            csrfToken={csrfToken}
            nowMs={nowMs}
            onDone={onDone}
            onError={onError}
          />
        ))}
      </div>
    </section>
  );
}

function InstrumentRow({
  instrument,
  holding,
  csrfToken,
  nowMs,
  onDone,
  onError,
}: {
  instrument: InstrumentDto;
  holding: HoldingDto | undefined;
  csrfToken: string | null;
  nowMs: number;
  onDone: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState<"buy" | "sell" | null>(null);

  async function act(action: "buy" | "sell") {
    setBusy(action);
    try {
      await api(`/api/investments/${action}`, {
        method: "POST",
        csrfToken,
        body: {
          instrumentId: instrument.id,
          amount,
        },
      });
      setAmount("");
      await onDone(action === "buy" ? "Investment bought." : "Investment sold.");
    } catch (error) {
      onError(error instanceof Error ? error.message : "Investment action failed");
    } finally {
      setBusy(null);
    }
  }

  return (
    <article className="instrument">
      <div>
        <div className="instrument-title">
          <h4>{instrument.name}</h4>
          <span className={`risk ${instrument.riskLevel}`}>{instrument.riskLevel}</span>
        </div>
        <p>{instrument.description}</p>
        <p className="muted">
          Weekly range: {bps(instrument.minWeeklyReturnBps)} to{" "}
          {bps(instrument.maxWeeklyReturnBps)}. Dip chance:{" "}
          {bps(instrument.lossProbabilityBps)}. Max dip: -{bps(instrument.maxWeeklyLossBps)}.
        </p>
        {holding ? (
          <div className="holding-details">
            <span>
              <small>Current value</small>
              <strong>{money(holding.currentValueCents)}</strong>
            </span>
            <span>
              <small>Next result</small>
              <strong>{timeRemaining(holding.nextResultAt, nowMs)}</strong>
            </span>
            <span>
              <small>Week step</small>
              <strong>
                {holding.weeklyStep}/{holding.weeklySteps}
              </strong>
            </span>
          </div>
        ) : null}
        {holding ? (
          <div
            className="week-step-rail"
            aria-label={`${instrument.name} simulated week step ${holding.weeklyStep} of ${holding.weeklySteps}`}
          >
            {Array.from({ length: holding.weeklySteps }, (_, index) => (
              <span
                key={index}
                className={index < holding.weeklyStep ? "active" : ""}
              />
            ))}
          </div>
        ) : null}
      </div>
      <div className="investment-actions">
        <input
          aria-label={`${instrument.name} amount`}
          inputMode="decimal"
          placeholder="10.00"
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
        <button className="secondary-button" disabled={busy !== null} onClick={() => act("buy")}>
          {busy === "buy" ? "Buying..." : "Buy"}
        </button>
        <button className="ghost-button" disabled={busy !== null || !holding} onClick={() => act("sell")}>
          {busy === "sell" ? "Selling..." : "Sell"}
        </button>
      </div>
    </article>
  );
}

function ParentRequests({
  requests,
  csrfToken,
  onDone,
  onError,
}: {
  requests: MoneyRequestDto[];
  csrfToken: string | null;
  onDone: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const pending = requests.filter((request) => request.status === "pending");

  async function review(id: string, action: "approve" | "deny") {
    try {
      await api(`/api/parent/requests/${id}/${action}`, {
        method: "POST",
        csrfToken,
      });
      await onDone(action === "approve" ? "Request approved." : "Request denied.");
    } catch (error) {
      onError(error instanceof Error ? error.message : "Review failed");
    }
  }

  return (
    <section className="panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Banker</p>
          <h3>Pending requests</h3>
        </div>
        <strong>{pending.length}</strong>
      </div>
      {pending.length === 0 ? (
        <p className="muted">No pending requests.</p>
      ) : (
        <div className="request-list">
          {pending.map((request) => (
            <article className="request-item" key={request.id}>
              <div>
                <strong>{request.type}</strong>
                <span>{money(request.amountCents)}</span>
                {request.childName ? <p>{request.childName}</p> : null}
                {request.note ? <p>{request.note}</p> : null}
              </div>
              <div className="request-actions">
                <button className="secondary-button" onClick={() => review(request.id, "approve")}>
                  Approve
                </button>
                <button className="ghost-button" onClick={() => review(request.id, "deny")}>
                  Deny
                </button>
              </div>
            </article>
          ))}
        </div>
      )}
    </section>
  );
}

function ChildManagement({
  children,
  csrfToken,
  onDone,
  onError,
}: {
  children: ChildAccountDto[];
  csrfToken: string | null;
  onDone: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [createdChild, setCreatedChild] = useState<ChildAccountDto | null>(null);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    setBusy(true);
    const form = new FormData(formElement);

    try {
      const result = await api<{ child: ChildAccountDto }>("/api/parent/children", {
        method: "POST",
        csrfToken,
        body: {
          name: String(form.get("name") ?? ""),
          password: String(form.get("password") ?? ""),
        },
      });
      setCreatedChild(result.child);
      formElement.reset();
      await onDone(`Child account created for ${result.child.displayName}.`);
    } catch (error) {
      onError(error instanceof Error ? error.message : "Child account failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Children</p>
          <h3>Manage access</h3>
        </div>
        <strong>{children.length}</strong>
      </div>
      <form className="compact-form" onSubmit={submit}>
        <label>
          Name
          <input name="name" autoComplete="name" maxLength={80} required />
        </label>
        <label>
          Password
          <input
            name="password"
            type="password"
            autoComplete="new-password"
            minLength={4}
            maxLength={32}
            required
          />
        </label>
        <button className="primary-button" disabled={busy || !csrfToken}>
          {busy ? "Creating..." : "Add child"}
        </button>
      </form>
      {createdChild ? (
        <div className="credential-callout">
          <span>Username</span>
          <strong>{createdChild.username}</strong>
        </div>
      ) : null}
      <div className="child-list">
        {children.length === 0 ? (
          <p className="muted">No children yet.</p>
        ) : (
          children.map((child) => (
            <article className="child-item" key={child.id}>
              <div>
                <strong>{child.displayName}</strong>
                <span>{child.accountName}</span>
              </div>
              <code>{child.username}</code>
            </article>
          ))
        )}
      </div>
    </section>
  );
}

function ParentEntry({
  csrfToken,
  onDone,
  onError,
}: {
  csrfToken: string | null;
  onDone: (message: string) => Promise<void>;
  onError: (message: string) => void;
}) {
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    setBusy(true);
    const form = new FormData(formElement);

    try {
      await api("/api/parent/ledger-entry", {
        method: "POST",
        csrfToken,
        body: {
          direction: String(form.get("direction") ?? "add"),
          amount: String(form.get("amount") ?? ""),
          description: String(form.get("description") ?? ""),
        },
      });
      formElement.reset();
      await onDone("Parent entry saved.");
    } catch (error) {
      onError(error instanceof Error ? error.message : "Entry failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="panel">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Direct entry</p>
          <h3>Add a correction</h3>
        </div>
      </div>
      <form className="compact-form" onSubmit={submit}>
        <label>
          Direction
          <select name="direction">
            <option value="add">Add money</option>
            <option value="subtract">Subtract money</option>
          </select>
        </label>
        <label>
          Amount
          <input name="amount" inputMode="decimal" placeholder="10.00" required />
        </label>
        <label>
          Description
          <input name="description" placeholder="Allowance, correction, cash received" required />
        </label>
        <button className="primary-button" disabled={busy}>
          {busy ? "Saving..." : "Save entry"}
        </button>
      </form>
    </section>
  );
}

function ActivityPanel({
  transactions,
  pendingRequests,
}: {
  transactions: LedgerEntryDto[];
  pendingRequests: MoneyRequestDto[];
}) {
  const [filter, setFilter] = useState<"all" | LedgerEntryDto["type"]>("all");
  const [showAll, setShowAll] = useState(false);
  const transactionTypes = useMemo(
    () => Array.from(new Set(transactions.map((transaction) => transaction.type))),
    [transactions],
  );
  const visibleTransactions = useMemo(
    () =>
      transactions.filter((transaction) =>
        filter === "all" ? true : transaction.type === filter,
      ),
    [filter, transactions],
  );
  const displayedTransactions = showAll
    ? visibleTransactions
    : visibleTransactions.slice(0, 5);

  return (
    <section className="panel activity-panel">
      <div className="activity-layout">
        <div className="activity-main">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Activity</p>
              <h3>Transactions</h3>
            </div>
            <label className="filter-label">
              <span>Filter transactions</span>
              <select
                value={filter}
                onChange={(event) => {
                  setFilter(event.target.value as "all" | LedgerEntryDto["type"]);
                  setShowAll(false);
                }}
              >
                <option value="all">All</option>
                {transactionTypes.map((type) => (
                  <option key={type} value={type}>
                    {humanType(type)}
                  </option>
                ))}
              </select>
            </label>
          </div>
          {pendingRequests.length > 0 ? (
            <div className="pending-strip">
              {pendingRequests.map((request) => (
                <span key={request.id}>
                  Pending {request.type}: {money(request.amountCents)}
                </span>
              ))}
            </div>
          ) : null}
          <div className="transaction-list">
            {visibleTransactions.length === 0 ? (
              <p className="muted">No transactions match this filter.</p>
            ) : (
              displayedTransactions.map((transaction) => (
                <article className="transaction" key={transaction.id}>
                  <div className={`transaction-icon ${transactionIconTone(transaction.type)}`}>
                    {transactionIcon(transaction.type)}
                  </div>
                  <div>
                    <strong>{humanType(transaction.type)}</strong>
                    <span>{transaction.description}</span>
                  </div>
                  <div className={transaction.amountCents >= 0 ? "money-up" : "money-down"}>
                    {money(transaction.amountCents)}
                  </div>
                </article>
              ))
            )}
          </div>
          {visibleTransactions.length > 5 ? (
            <button className="view-all-button" onClick={() => setShowAll((current) => !current)}>
              {showAll ? "Show fewer transactions" : "View all transactions"}⌄
            </button>
          ) : null}
        </div>
        <aside className="encouragement" aria-label="Savings encouragement">
          <div className="encouragement-bubble">
            <span aria-hidden="true">💡</span>
            <strong>Great job!</strong>
            <p>You're building a smart future.</p>
          </div>
          <div className="mascot" aria-hidden="true">
            🧒
          </div>
        </aside>
      </div>
    </section>
  );
}

function AppHeader({
  user,
  csrfToken,
  onLogout,
}: {
  user: AuthUser;
  csrfToken: string | null;
  onLogout: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  return (
    <header className="app-header">
      <div className="brand-lockup">
        <span className="brand-icon" aria-hidden="true">
          👛
        </span>
        <h1>Edfolio</h1>
      </div>
      <div className="header-actions">
        <div className="user-greeting">
          <span className="avatar" aria-hidden="true">
            {user.role === "child" ? "🧒" : "🧑‍💼"}
          </span>
          <strong>{user.role === "child" ? `Hi, ${user.displayName}` : "Parent banker"}</strong>
        </div>
        <button
          className="ghost-button logout-button"
          disabled={busy || !csrfToken}
          onClick={async () => {
            setBusy(true);
            await onLogout();
            setBusy(false);
          }}
        >
          <span aria-hidden="true">↪</span>
          Log out
        </button>
      </div>
    </header>
  );
}

function StatusBanner({ status }: { status: Status }) {
  return <div className={`status ${status.kind}`}>{status.message}</div>;
}

function EmptyState() {
  return (
    <section className="panel">
      <h2>No account yet</h2>
      <p className="muted">Create the first family account with the setup API.</p>
    </section>
  );
}

function LoadingScreen() {
  return (
    <main className="loading-screen">
      <div className="loader" />
      <p>Opening Edfolio...</p>
    </main>
  );
}

async function api<T = { ok: boolean }>(
  path: string,
  options: {
    method?: string;
    body?: unknown;
    csrfToken?: string | null;
  } = {},
): Promise<T> {
  const response = await fetch(path, {
    method: options.method ?? "GET",
    credentials: "include",
    headers: {
      ...(options.body ? { "content-type": "application/json" } : {}),
      ...(options.csrfToken ? { "x-csrf-token": options.csrfToken } : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });

  const contentType = response.headers.get("content-type") ?? "";
  const payload = contentType.includes("application/json")
    ? ((await response.json()) as { error?: string } | null)
    : null;

  if (!response.ok) {
    throw new Error(payload?.error ?? `Request failed with ${response.status}`);
  }

  return payload as T;
}

function graphPoints(history: BalanceSnapshotDto[]) {
  if (history.length === 0) {
    return { line: "", area: "", circles: [] as { x: number; y: number }[] };
  }

  const totals = history.map((item) => item.totalCents);
  const min = Math.min(...totals, 0);
  const max = Math.max(...totals, 100);
  const span = Math.max(1, max - min);
  const left = 58;
  const right = 610;
  const top = 48;
  const bottom = 262;

  const circles = history.map((item, index) => {
    const x = history.length === 1 ? (left + right) / 2 : left + ((right - left) * index) / (history.length - 1);
    const y = bottom - ((item.totalCents - min) / span) * (bottom - top);
    return { x, y };
  });
  const line = circles.map((point, index) => `${index === 0 ? "M" : "L"} ${point.x} ${point.y}`).join(" ");
  const area = `${line} L ${circles.at(-1)?.x ?? right} ${bottom} L ${circles[0].x} ${bottom} Z`;

  return { line, area, circles };
}

function money(amountCents: number) {
  return currency.format(amountCents / 100);
}

function bps(value: number) {
  return `${(value / 100).toFixed(2)}%`;
}

function soonestNextResultAt(holdings: HoldingDto[]): string | null {
  let soonest: string | null = null;
  let soonestMs = Number.POSITIVE_INFINITY;

  for (const holding of holdings) {
    const nextResultMs = Date.parse(holding.nextResultAt);
    if (Number.isFinite(nextResultMs) && nextResultMs < soonestMs) {
      soonest = holding.nextResultAt;
      soonestMs = nextResultMs;
    }
  }

  return soonest;
}

function timeRemaining(nextResultAt: string, nowMs: number): string {
  const nextResultMs = Date.parse(nextResultAt);
  if (!Number.isFinite(nextResultMs)) {
    return "--";
  }

  const remainingSeconds = Math.max(0, Math.ceil((nextResultMs - nowMs) / 1000));
  const days = Math.floor(remainingSeconds / 86_400);
  const hours = Math.floor((remainingSeconds % 86_400) / 3_600);
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;

  if (days > 0) {
    return `${days}d ${hours}h`;
  }

  if (hours > 0) {
    return `${hours}h ${Math.floor((remainingSeconds % 3_600) / 60)}m`;
  }

  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

function humanType(type: LedgerEntryDto["type"]): string {
  return type
    .split("_")
    .map((part) => part[0].toUpperCase() + part.slice(1))
    .join(" ");
}

function transactionIcon(type: LedgerEntryDto["type"]): string {
  switch (type) {
    case "deposit":
      return "↓";
    case "withdrawal":
      return "↑";
    case "investment_buy":
      return "↗";
    case "investment_sell":
      return "↙";
    case "investment_return":
      return "↗";
    case "parent_adjustment":
      return "±";
  }
}

function transactionIconTone(type: LedgerEntryDto["type"]): string {
  if (type === "withdrawal" || type === "investment_buy") {
    return "out";
  }

  if (type === "investment_return" || type === "investment_sell") {
    return "growth";
  }

  return "in";
}
